import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, CircleSlash, Loader2, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/context/ToastContext";
import { api, unwrapArray } from "@/lib/api";
import { cn } from "@/lib/utils";

export interface PlannedView {
  id: string;
  key: string;
  status: "PLANNED" | "DONE" | "FAILED" | "CANCELLED" | "EXPIRED";
  title: string;
  lines: string[];
  result?: { type: string; id: string; label: string; link: string | null } | null;
  error?: string | null;
}

/**
 * The changes the AI assistant prepared: each line says what will happen (before → after). Nothing is saved until the
 * person presses Confirm; they can untick any line first. Afterwards each line shows it was made (with a link) or why not.
 */
export function PlannedActions({ actions, onChange }: { actions: PlannedView[]; onChange: (next: PlannedView[]) => void }) {
  const { success, error } = useToast();
  const waiting = actions.filter((a) => a.status === "PLANNED");
  const [picked, setPicked] = useState<Set<string>>(() => new Set(waiting.map((a) => a.id)));
  const [busy, setBusy] = useState<"confirm" | "cancel" | null>(null);

  const merge = (rows: PlannedView[]) => onChange(actions.map((a) => rows.find((r) => r.id === a.id) || a));

  const confirm = async () => {
    const ids = waiting.filter((a) => picked.has(a.id)).map((a) => a.id);
    if (!ids.length) return;
    setBusy("confirm");
    try {
      const rows = unwrapArray<PlannedView>(await api.post("/api/AiAction/Confirm", { action_ids: ids }));
      merge(rows);
      // Unticked ones are dropped, so the assistant doesn't count them as waiting
      const left = waiting.filter((a) => !picked.has(a.id)).map((a) => a.id);
      if (left.length) merge([...rows, ...unwrapArray<PlannedView>(await api.post("/api/AiAction/Cancel", { action_ids: left }))]);
      const failed = rows.filter((r) => r.status === "FAILED").length;
      if (failed) error("Not everything was saved", "See the card for what didn't work");
      else success("Saved", `${rows.filter((r) => r.status === "DONE").length} change${rows.length === 1 ? "" : "s"} made`);
    } catch (e: any) {
      error("Couldn't save the changes", e.message || "Please try again");
    } finally {
      setBusy(null);
    }
  };
  const cancel = async () => {
    setBusy("cancel");
    try {
      merge(unwrapArray<PlannedView>(await api.post("/api/AiAction/Cancel", { action_ids: waiting.map((a) => a.id) })));
    } catch (e: any) {
      error("Couldn't cancel", e.message || "Please try again");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="max-w-xl rounded-xl border border-primary/30 bg-card" aria-label="Changes to check">
      <div className="border-b border-border px-3.5 py-2 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{waiting.length ? "Check these changes" : "Changes"}</div>
      <ul className="divide-y divide-border">
        {actions.map((a) => {
          const on = picked.has(a.id);
          return (
            <li key={a.id} className={cn("flex gap-2.5 px-3.5 py-2.5 text-[13px]", a.status === "PLANNED" && !on && "opacity-60")}>
              <span className="mt-0.5 shrink-0">
                {a.status === "PLANNED" ? (
                  <input
                    type="checkbox"
                    checked={on}
                    aria-label={`Include: ${a.title}`}
                    onChange={() =>
                      setPicked((cur) => {
                        const n = new Set(cur);
                        n.has(a.id) ? n.delete(a.id) : n.add(a.id);
                        return n;
                      })
                    }
                    className="size-4 cursor-pointer accent-primary"
                  />
                ) : a.status === "DONE" ? (
                  <Check className="size-4 text-success" />
                ) : a.status === "FAILED" ? (
                  <TriangleAlert className="size-4 text-destructive" />
                ) : (
                  <CircleSlash className="size-4 text-muted-foreground" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{a.title}</span>
                {a.lines.length > 0 && (
                  <ul className="mt-0.5 space-y-0.5 text-xs text-muted-foreground">
                    {a.lines.map((l, i) => (
                      <li key={i}>{l}</li>
                    ))}
                  </ul>
                )}
                {a.status === "DONE" && a.result?.link && (
                  <Link to={a.result.link} className="mt-1 inline-block text-xs font-medium text-primary hover:underline">
                    Open {String(a.result.type || "record").toLowerCase()}
                  </Link>
                )}
                {a.status === "FAILED" && <span className="mt-1 block text-xs text-destructive">{a.error || "It couldn't be saved"}</span>}
                {(a.status === "CANCELLED" || a.status === "EXPIRED") && <span className="mt-1 block text-xs text-muted-foreground">{a.status === "EXPIRED" ? a.error || "Prepared too long ago; ask again" : "Not made"}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {waiting.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-3.5 py-2.5">
          <Button size="sm" className="gap-1.5" disabled={!!busy || !waiting.some((a) => picked.has(a.id))} onClick={() => void confirm()}>
            {busy === "confirm" ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />} Confirm {waiting.filter((a) => picked.has(a.id)).length > 1 ? `${waiting.filter((a) => picked.has(a.id)).length} changes` : ""}
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => void cancel()}>
            Cancel
          </Button>
          <span className="text-[11.5px] text-muted-foreground">Nothing is saved until you confirm. Or tell me what to change.</span>
        </div>
      )}
    </section>
  );
}
